import { useState } from 'react';
import { View } from 'react-native';
import { formatISO } from 'date-fns';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Text } from '@/components/ui/text';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/date-picker/DatePicker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { BaseModalProps } from '@/contexts/ModalContext';
import { usePowerSync } from '~/powersync/contexts/power-sync/PowerSync';
import { db } from '@/lib/local-api';
import { useToast } from '~/global/contexts/toast/Toast';

export interface DuplicateWorkoutModalProps extends BaseModalProps {
  workoutId: string;
  workoutName: string;
  /** Pre-selected date; defaults to today. */
  defaultDate?: Date;
  portalHost?: string;
}

/**
 * Prompts for a date, then duplicates the workout into a new planned session on
 * that date (keeping the original). Surfaces a success toast on completion; the
 * copy then appears in the list via the live query.
 */
export const DuplicateWorkoutModal = ({
  onClose,
  workoutId,
  workoutName,
  defaultDate,
  portalHost,
}: DuplicateWorkoutModalProps) => {
  const { t } = useTranslation('workout');
  const powerSync = usePowerSync();
  const { toast } = useToast();
  const [date, setDate] = useState<Date>(defaultDate ?? new Date());

  const duplicateMutation = useMutation({
    mutationFn: () =>
      db.Workout.mutations.duplicateWorkout(powerSync, workoutId, {
        plannedAt: formatISO(date, { representation: 'date' }),
      }),
    onSuccess: () => {
      toast({
        variant: 'success',
        title: t('duplicate.toast.title'),
        description: t('duplicate.toast.description', { name: workoutName }),
      });
      onClose();
    },
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent portalHost={portalHost}>
        <DialogHeader>
          <DialogTitle>{t('duplicate.modal.title')}</DialogTitle>
          <DialogDescription>
            {t('duplicate.modal.description')}
          </DialogDescription>
        </DialogHeader>

        <View className="py-2">
          <DatePicker
            value={date}
            onChange={setDate}
            label={t('duplicate.modal.dateLabel')}
          />
        </View>

        <DialogFooter>
          <Button
            variant="secondary"
            className="flex-1"
            onPress={onClose}
            disabled={duplicateMutation.isPending}
          >
            <Text>{t('duplicate.modal.cancel')}</Text>
          </Button>
          <Button
            testID="workout-duplicate-confirm"
            className="flex-1"
            onPress={() => duplicateMutation.mutate()}
            loading={duplicateMutation.isPending}
          >
            <Text>{t('duplicate.modal.confirm')}</Text>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
